import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, X, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset your password — Lumvio" },
      {
        name: "description",
        content:
          "Choose a new password for your Lumvio account. Enter and confirm your new password to regain secure access to your workspace.",
      },
      { property: "og:title", content: "Reset your Lumvio password" },
      { property: "og:description", content: "Set a new password to regain access to your Lumvio workspace." },
      { property: "og:url", content: "https://lumvio.co/reset-password" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://lumvio.co/reset-password" }],
  }),
  component: ResetPasswordPage,
});

const RULES = [
  { key: "len", label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter (A–Z)", test: (p: string) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a–z)", test: (p: string) => /[a-z]/.test(p) },
  { key: "num", label: "One number (0–9)", test: (p: string) => /\d/.test(p) },
  { key: "sym", label: "One symbol (!@#$…)", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
] as const;

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Supabase routes the recovery link with a session already established
  // in the URL hash. Wait for the SDK to hydrate it, then allow updates.
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setReady(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        setReady(true);
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const score = useMemo(() => RULES.reduce((n, r) => n + (r.test(password) ? 1 : 0), 0), [password]);
  const allRulesMet = score === RULES.length;
  const passwordsMatch = password.length > 0 && password === confirm;
  const canSubmit = ready && allRulesMet && passwordsMatch && !busy;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setDone(true);
    toast.success("Password updated");
    setTimeout(() => navigate({ to: "/dashboard" }), 1200);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-5 rounded-2xl border bg-card p-8 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Set a new password</h1>
            <p className="text-xs text-muted-foreground">Choose a strong password to secure your account.</p>
          </div>
        </div>

        {!ready && !done && (
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 text-sm text-yellow-700 dark:text-yellow-400">
            Waiting for a valid recovery link… If you got here directly, open the reset link from your email again.
          </div>
        )}

        {done ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
            <div className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
              <Check className="h-4 w-4" /> Password updated
            </div>
            <p className="mt-1 text-muted-foreground">Redirecting you to the dashboard…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw">New password</Label>
              <div className="relative">
                <Input
                  id="rp-pw"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pe-10"
                  disabled={!ready}
                />
                <button
                  type="button"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <ul className="mt-1 grid grid-cols-1 gap-0.5 text-[11px]">
                {RULES.map((r) => {
                  const ok = r.test(password);
                  return (
                    <li
                      key={r.key}
                      className={`flex items-center gap-1.5 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}
                    >
                      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {r.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rp-confirm">Confirm password</Label>
              <Input
                id="rp-confirm"
                type={showPw ? "text" : "password"}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={!ready}
                className={
                  confirm.length === 0
                    ? ""
                    : passwordsMatch
                      ? "border-emerald-500/60 focus-visible:ring-emerald-500/40"
                      : "border-destructive/60 focus-visible:ring-destructive/40"
                }
              />
              {confirm.length > 0 && !passwordsMatch && (
                <p className="text-[11px] text-destructive">Passwords don't match</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {busy ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update password"
              )}
            </Button>

            <div className="text-center text-xs">
              <Link to="/auth" className="text-muted-foreground hover:text-foreground">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
